import React, { useContext } from 'react';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { format, parseISO } from 'date-fns';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import { useLocation, useNavigate } from 'react-router-dom';

import { DataLoadingCard, MDropdown, NoAvailableCard } from 'common';
import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';

import { useGetLoadingPercentageQuery } from './terminalAllocationLoadingPercentageEndpoints';

const AffiliateSwitcher = () => {
  const { currentAffiliate } = useUserRolePermissions();
  const affiliatesOptions = useGetAffiliateOptions([
    'inventoryOwner',
    'CASR-Product-Ownership: Manage',
  ]);
  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    const p = pathname.split('/');
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/${p[1]}/${affiliate?.customerIdName}/${affiliate?.dataCompanyId}/${p.slice(4).join('/')}`,
      {
        replace: true,
      }
    );
  };

  return affiliatesOptions.length > 1 ? (
    <Box>
      <MDropdown
        id="affiliate-select"
        items={affiliatesOptions}
        label="Affiliate"
        name="affiliate"
        onChange={({ value }) => handleAffiliateChange(value)}
        style={{ padding: 5 }}
        value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
      />
    </Box>
  ) : (
    <></>
  );
};

const CustomToolbar = () => (
  <GridToolbarContainer>
    <AffiliateSwitcher />
    <GridToolbarColumnsButton variant="dataTable" />
    <GridToolbarFilterButton variant="dataTable" />
    <GridToolbarDensitySelector variant="dataTable" />
    <GridToolbarExport variant="dataTable" />
  </GridToolbarContainer>
);

const TerminalAllocationLoadingPercentage = () => {
  const { currentAffiliate } = useUserRolePermissions();
  const percentFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'percent',
  });
  const minWidth = 150;
  const columns = [
    {
      aggregable: false,
      field: 'locationId',
      headerName: 'Location ID',
      minWidth,
    },
    {
      aggregable: false,
      field: 'locationNameLong',
      headerName: 'Location',
      minWidth,
    },
    {
      aggregable: false,
      field: 'productId',
      headerName: 'Product ID',
      minWidth,
    },
    {
      aggregable: false,
      field: 'productNameLong',
      headerName: 'Product Name',
      width: 450,
    },
    {
      aggregable: false,
      field: 'inventoryOwnerId',
      headerName: 'Inventory Owner ID',
      minWidth,
    },
    {
      aggregable: false,
      field: 'inventoryOwnerNameLong',
      headerName: 'Inventory Owner',
      minWidth,
    },
    {
      field: 'loadingPercentage',
      headerName: 'Loading Percentage',
      minWidth,
      type: 'number',
      valueFormatter: ({ value }) => {
        if (!value) {
          return value;
        }
        return percentFormatter.format(value);
      },
    },
    {
      aggregable: false,
      field: 'dateCreated',
      headerName: 'Date Created',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('dateCreated', row)
          ? format(parseISO(R.prop('dateCreated', row)), 'MM/dd/yyyy')
          : null,
    },
  ];

  const { data, isFetching } = useGetLoadingPercentageQuery({
    affiliate: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  });

  return (
    <div style={{ padding: 1, width: '100%' }}>
      <div style={{ height: 'calc(100vh - 70px)', marginTop: '50px', padding: 30, width: '100%' }}>
        {isFetching ? (
          <Box
            sx={{
              p: theme => ({ lg: theme.spacing(4, 1, 1), xs: theme.spacing(9, 3, 4) }),
              textAlign: 'center',
            }}
          >
            <DataLoadingCard type="data" />
          </Box>
        ) : (
          <Box
            sx={{
              height: 'calc(100vh - 81px)',
            }}
          >
            <DataGridPremium
              columns={columns}
              getRowId={r => `${r.locationId}_${r.inventoryOwnerId}_${r.dateCreated}`}
              initialState={{
                aggregation: {
                  model: {
                    loadingPercentage: 'sum',
                  },
                },
              }}
              rows={data}
              rowSelection={false}
              slots={{
                loadingOverlay: () => (
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  <Box
                    style={{
                      height: '100%',
                    }}
                    sx={{ mt: { lg: 0, xs: 6 } }}
                  >
                    <DataLoadingCard type="data" />
                  </Box>
                ),
                noRowsOverlay: () => (
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  <Box
                    sx={{
                      mt: { lg: 0, xs: 6 },
                    }}
                  >
                    <NoAvailableCard type="data" />
                  </Box>
                ),
                toolbar: CustomToolbar,
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default TerminalAllocationLoadingPercentage;
